import axios from "axios";
import { toast } from "react-toastify";

const ADMIN_SESSION_STARTED_AT_KEY = "adminSessionStartedAt";
const EMPLOYEE_SESSION_STARTED_AT_KEY = "employeeSessionStartedAt";

// Create axios instance with base configuration
// Prefer REACT_APP_BACKEND_URL when provided; otherwise fallback to same-origin
const resolveBaseURL = () => {
  const envUrlRaw = process.env.REACT_APP_BACKEND_URL?.trim();
  const currentOrigin = window.location.origin;

  if (!envUrlRaw) return currentOrigin;
  try {
    const envUrl = new URL(envUrlRaw);
    return envUrl.origin;
  } catch {
    return currentOrigin;
  }
};

const resolvedBaseURL = resolveBaseURL();

const axiosInstance = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 10000,
});

const resolveSessionTtlMs = () => {
  const raw = process.env.REACT_APP_SESSION_TTL_SECONDS?.trim();
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return seconds * 1000;
};

const sessionTtlMs = resolveSessionTtlMs();

// Helper: get token from localStorage whether saved as raw string or JSON
const getTokenFromStorage = () => {
  const adminSession = localStorage.getItem("adminSession");
  const employeeSession = localStorage.getItem("employeeSession");

  const normalizeToken = (value) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "string") return parsed;
      if (parsed && typeof parsed === "object") {
        return parsed.token || parsed.accessToken || parsed.authToken || null;
      }
      return null;
    } catch (e) {
      return value; // not JSON, raw token
    }
  };

  return normalizeToken(adminSession) || normalizeToken(employeeSession);
};

const getSessionStartedAt = () => {
  const adminStartedAtRaw = localStorage.getItem(ADMIN_SESSION_STARTED_AT_KEY);
  const employeeStartedAtRaw = localStorage.getItem(
    EMPLOYEE_SESSION_STARTED_AT_KEY,
  );

  const parse = (value) => {
    if (!value) return null;
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  };

  return parse(adminStartedAtRaw) || parse(employeeStartedAtRaw);
};

const shouldForceExpireSession = () => {
  if (!sessionTtlMs) return false;
  const startedAt = getSessionStartedAt();
  if (!startedAt) return false;
  return Date.now() - startedAt >= sessionTtlMs;
};

// Request interceptor to add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getTokenFromStorage();

    if (token && shouldForceExpireSession() && !isRedirectingDueTo401) {
      isRedirectingDueTo401 = true;
      handleTokenExpiration();
      return Promise.reject(new Error("Session expired (client TTL)"));
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

let isRedirectingDueTo401 = false;

// Response interceptor to handle token expiration globally
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (!error.response) {
      toast.error(
        "Network Error: Unable to connect to the server. Please check your internet connection.",
        {
          toastId: "networkError",
        },
      );
    } else if (error.response?.status === 401 && !isRedirectingDueTo401) {
      // Token expired or unauthorized
      isRedirectingDueTo401 = true;
      handleTokenExpiration();
    }
    return Promise.reject(error);
  },
);

// Function to handle token expiration
const handleTokenExpiration = () => {
  // Clear all sessions
  localStorage.removeItem("adminSession");
  localStorage.removeItem("employeeSession");
  localStorage.removeItem(ADMIN_SESSION_STARTED_AT_KEY);
  localStorage.removeItem(EMPLOYEE_SESSION_STARTED_AT_KEY);
  localStorage.removeItem("userRole");
  localStorage.removeItem("id");

  // Show toast notification
  toast.error("Session expired. Please login again.", {
    toastId: "sessionExpired",
    autoClose: 3000,
  });

  // Redirect immediately to avoid being stuck on protected routes
  window.location.replace("/login");
};

// Also attach global interceptors to the default axios (for modules using axios directly)
axios.interceptors.request.use(
  (config) => {
    const token = getTokenFromStorage();
    if (token && shouldForceExpireSession() && !isRedirectingDueTo401) {
      isRedirectingDueTo401 = true;
      handleTokenExpiration();
      return Promise.reject(new Error("Session expired (client TTL)"));
    }
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      toast.error(
        "Network Error: Unable to connect to the server. Please check your internet connection.",
        {
          toastId: "networkError",
        },
      );
    } else if (error.response?.status === 401 && !isRedirectingDueTo401) {
      isRedirectingDueTo401 = true;
      handleTokenExpiration();
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
